import { motion } from "framer-motion";
import {
	BarChart3,
	Briefcase,
	Building2,
	ClipboardList,
	CreditCard,
	FileText,
	Gift,
	LogIn,
	LogOut,
	Newspaper,
	Rocket,
	Settings,
	Sparkles,
	UserCircle,
	UserPlus,
	Users,
} from "lucide-react";
import React from "react";
import { Link, useLocation } from "react-router-dom";

import { HomeIcon } from "../../../assets/icons/interface/interface";
import CompanyJobPostInstructionsModal from "../../../components/CompanyJobPostInstructionsModal";
import ProfileAvatar from "../../../components/ProfileAvatar";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";
import { useAuth } from "../../../context/Auth/AuthContext";
import { Button } from "../../JobsPage/components/Button";
import JobStatusBadge from "./JobStatusBadge";

// Navigation items
const defaultNavItems = [
	{ item: "Jobs", route: "/jobs", icon: Briefcase },
	{ item: "Companies", route: "/companies", icon: Building2 },
	{ item: "Articles", route: "/articles", icon: Newspaper },
	{ item: "For Employers", route: "/for-employers", icon: Users },
	{ item: "FitCheckMe", route: "/fitcheckme", icon: Sparkles },
	{ item: "About us", route: "/about-us", icon: UserCircle },
	{ item: "Plans", route: "/payments", icon: CreditCard },
];

const applicantNavItems = [
	{ item: "Profile", route: "/profile", icon: UserCircle },
	{ item: "Jobs", route: "/jobs", icon: Briefcase },
	{ item: "Companies", route: "/companies", icon: Building2 },
	{ item: "Applications", route: "/applications", icon: ClipboardList },
	{ item: "FitCheck Insights", route: "/fitchecks", icon: BarChart3 },
];

// --- Sub-components ---

const HamburgerTrigger = React.forwardRef(({ picture, ...props }, ref) => (
	<Button ref={ref} variant="ghost" className="flex items-center space-x-2" {...props}>
		<div className="w-4 h-4 flex flex-col justify-between">
			<span className="w-full h-0.5 bg-gray-800 rounded-full" />
			<span className="w-full h-0.5 bg-gray-800 rounded-full" />
			<span className="w-full h-0.5 bg-gray-800 rounded-full" />
		</div>
		{picture && (
			<div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
				<ProfileAvatar src={picture} width={32} height={32} className="rounded-full object-cover" />
			</div>
		)}
	</Button>
));
HamburgerTrigger.displayName = "HamburgerTrigger";

function LoggedInMenuItems({ logout }) {
	return (
		<>
			<DropdownMenuItem asChild>
				<Link to="/profile">
					<Rocket className="mr-2 h-4 w-4 text-gray-500" />
					Smart Profile
				</Link>
			</DropdownMenuItem>
			<DropdownMenuItem asChild>
				<Link to="/my-resumes">
					<FileText className="mr-2 h-4 w-4 text-gray-500" />
					My Resumes
				</Link>
			</DropdownMenuItem>
			<DropdownMenuItem asChild>
				<Link to="/rewards">
					<Gift className="mr-2 h-4 w-4 text-gray-500" />
					Rewards & Plan
				</Link>
			</DropdownMenuItem>
			<DropdownMenuItem asChild>
				<Link to="/settings">
					<Settings className="mr-2 h-4 w-4 text-gray-500" />
					Settings
				</Link>
			</DropdownMenuItem>
			<DropdownMenuSeparator className="bg-gray-200" />
			<DropdownMenuItem
				onSelect={() => {
					logout();
					window.location.href = "/";
				}}
			>
				<LogOut className="mr-2 h-4 w-4 text-gray-500" />
				Logout
			</DropdownMenuItem>
		</>
	);
}

function MobileNavMenuItems({ items, hiddenClass }) {
	return items.map((navItem) => {
		const Icon = navItem.icon;
		return (
			<DropdownMenuItem key={navItem.item} asChild className={hiddenClass}>
				<Link to={navItem.route}>
					{Icon && <Icon className="mr-2 h-4 w-4 text-gray-500" />}
					{navItem.item}
				</Link>
			</DropdownMenuItem>
		);
	});
}

function NavBar({
	navRef,
	items,
	hoveredLink,
	setHoveredLink,
	backgroundStyles,
	isNavItemSelected,
	breakpointClass,
	className = "",
}) {
	return (
		<nav ref={navRef} className={`${breakpointClass} space-x-1 2xl:space-x-2 relative ${className}`}>
			<motion.div
				className="absolute bg-gray-100 rounded-md z-0"
				initial={false}
				animate={{
					...backgroundStyles,
					height: "36px",
					top: "50%",
					y: "-50%",
				}}
				transition={{
					type: "spring",
					stiffness: 500,
					damping: 30,
				}}
			/>
			{items.map((item) => (
				<Button
					key={item.item}
					variant="ghost"
					asChild
					className={`h-9 relative z-10 px-3 2xl:px-4 ${isNavItemSelected(item.item) ? "bg-gray-100" : ""}`}
				>
					<Link
						to={item.route}
						target={item.type === "external" ? "_blank" : ""}
						className={`font-inter text-[14px] font-medium transition-colors rounded-md whitespace-nowrap ${
							isNavItemSelected(item.item) ? "text-gray-900" : "text-gray-700 hover:text-gray-900"
						}`}
						onMouseEnter={() => setHoveredLink(item.item)}
						onMouseLeave={() => setHoveredLink(null)}
						data-name={item.item}
					>
						{item.item}
					</Link>
				</Button>
			))}
		</nav>
	);
}

// --- Main Component ---

function Header({ variant = "default" }) {
	const { pathname } = useLocation();
	const [hoveredLink, setHoveredLink] = React.useState(null);
	const [backgroundStyles, setBackgroundStyles] = React.useState({});
	const [isJobPostModalOpen, setIsJobPostModalOpen] = React.useState(false);
	const navRef = React.useRef(null);

	const { isLoggedIn, userData, logout } = useAuth();
	const picture = userData?.picture || "";

	const isApplicant = variant === "applicant";
	const navItems = isApplicant ? applicantNavItems : defaultNavItems;

	// The default nav is seven links sitting next to two CTAs, which needs ~1200px of
	// header before the links start colliding with the buttons. The applicant nav is
	// five links next to one badge, so it can appear a breakpoint earlier. Below these
	// widths the same links are served from the hamburger dropdown.
	const desktopNavClass = isApplicant ? "hidden lg:flex" : "hidden xl:flex";
	const dropdownNavClass = isApplicant ? "lg:hidden" : "xl:hidden";

	const isNavItemSelected = (item) => {
		if (isApplicant) {
			return pathname === `/${item.toLowerCase()}`;
		}
		if (item === "Jobs") return pathname === "/jobs";
		if (item === "Companies") return pathname === "/companies";
		return pathname === `/${item.toLowerCase().replace(" ", "-")}`;
	};

	React.useEffect(() => {
		if (hoveredLink && navRef.current) {
			const linkElement = navRef.current.querySelector(`[data-name="${hoveredLink}"]`);
			if (linkElement) {
				const { offsetLeft, offsetWidth } = linkElement;
				setBackgroundStyles({
					left: `${offsetLeft}px`,
					width: `${offsetWidth}px`,
					opacity: 1,
				});
			}
		} else {
			setBackgroundStyles({ opacity: 0 });
		}
	}, [hoveredLink]);

	return (
		<>
			<header
				className={`h-16 bg-white flex items-center gap-2 px-4 md:px-8 ${
					isApplicant ? "justify-between" : "justify-end md:justify-between border-gray-200"
				}`}
			>
				{/* LEFT: Logo/Home + Nav */}
				{isApplicant ? (
					<div className="flex items-center space-x-4">
						<Link to="/">
							<Button variant="ghost" className="p-2">
								<HomeIcon className="w-6 h-6 text-gray-600 hover:text-gray-900" />
							</Button>
						</Link>
						<NavBar
							navRef={navRef}
							items={navItems}
							hoveredLink={hoveredLink}
							setHoveredLink={setHoveredLink}
							backgroundStyles={backgroundStyles}
							isNavItemSelected={isNavItemSelected}
							breakpointClass={desktopNavClass}
							className="justify-start"
						/>
					</div>
				) : (
					<>
						<div className="hidden md:flex items-center flex-1">
							<Link
								to="/"
								className="text-2xl font-bold text-gray-800 hover:text-gray-900 transition-colors font-plus-jakarta"
							>
								<img src="/images/home/logo.svg" alt="Dproz logo" className="w-24 h-auto text-gray-900" />
							</Link>
						</div>
						<NavBar
							navRef={navRef}
							items={navItems}
							hoveredLink={hoveredLink}
							setHoveredLink={setHoveredLink}
							backgroundStyles={backgroundStyles}
							isNavItemSelected={isNavItemSelected}
							breakpointClass={desktopNavClass}
							className="justify-center shrink-0"
						/>
					</>
				)}

				{/* RIGHT: Actions */}
				<div className={`flex items-center justify-end space-x-2 ${!isApplicant ? "flex-1" : ""}`}>
					{/* No status props: the badge reads and writes the account cache itself, so it works the
					    same on every page that renders the applicant header rather than only where a save
					    handler happened to be passed down. */}
					{isApplicant && (
						<div className="mr-4">
							<JobStatusBadge />
						</div>
					)}

					{isLoggedIn ? (
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<HamburgerTrigger picture={picture} />
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end" className="w-48">
								<MobileNavMenuItems items={navItems} hiddenClass={dropdownNavClass} />
								<DropdownMenuSeparator className={dropdownNavClass} />
								<LoggedInMenuItems logout={logout} />
							</DropdownMenuContent>
						</DropdownMenu>
					) : (
						<div className="flex items-center space-x-2">
							{pathname !== "/resume-tips" && (
								<Button
									variant="ghost"
									className="bg-gray-200 hover:bg-gray-300 text-gray-700 h-9 font-inter text-[14px] font-medium transition-colors flex items-center space-x-1"
									asChild
								>
									<Link to="/resume/select-experience" className="font-inter text-[14px] font-medium text-nowrap">
										Create Resume
									</Link>
								</Button>
							)}
							<Button
								variant="default"
								onClick={() => setIsJobPostModalOpen(true)}
								className="hidden md:inline-flex bg-primary-700 hover:bg-primary-800 group text-white h-9 text-nowrap"
							>
								Post a Job
							</Button>
							<Link to="/register" className="shrink-0">
								<UserCircle className="w-7 h-7 text-gray-600 hover:text-gray-900" />
							</Link>
							<div className={dropdownNavClass}>
								<DropdownMenu>
									<DropdownMenuTrigger asChild>
										<HamburgerTrigger />
									</DropdownMenuTrigger>
									<DropdownMenuContent align="end" className="w-[calc(100vw-2rem)] sm:w-56">
										<MobileNavMenuItems items={defaultNavItems} hiddenClass={dropdownNavClass} />
										<DropdownMenuSeparator />
										<DropdownMenuItem asChild>
											<Link to="/signin">
												<LogIn className="mr-2 h-4 w-4 text-gray-500" />
												Login
											</Link>
										</DropdownMenuItem>
										<DropdownMenuItem asChild>
											<Link to="/register">
												<UserPlus className="mr-2 h-4 w-4 text-gray-500" />
												Register
											</Link>
										</DropdownMenuItem>
									</DropdownMenuContent>
								</DropdownMenu>
							</div>
						</div>
					)}
				</div>
			</header>
			<CompanyJobPostInstructionsModal isOpen={isJobPostModalOpen} onClose={() => setIsJobPostModalOpen(false)} />
		</>
	);
}

export default Header;
